import * as React from 'react';
import { storyblokEditable } from 'gatsby-source-storyblok';
import { Link } from 'gatsby';
import { useLocation } from '@reach/router';
import Container from '../Container';
import FancyTitle from '../fancy-title';
import { extractLanguage, getStoryblokAlt } from '../../helpers/common';

const getStoryblokDimensions = (filename) => {
  const [width, height] = filename?.split('/')[5]?.split('x') || [];
  return { width, height };
};

const getStoryblokImageUrl = (filename, width = 800, format = 'webp') => {
  if (!filename || filename.includes('.svg') || !filename.includes('a.storyblok.com')) {
    return filename;
  }

  return `${filename}/m/${width}x0/filters:format(${format})`;
};

const getStoryblokImageSrcSet = (filename, widths, format = 'webp') => {
  if (!filename || filename.includes('.svg') || !filename.includes('a.storyblok.com')) {
    return undefined;
  }

  return widths.map((width) => `${getStoryblokImageUrl(filename, width, format)} ${width}w`).join(', ');
};

export default function PartnerReferencesSection({ blok }) {
  const location = useLocation();
  const currentLanguage = extractLanguage(location.pathname);
  const tiles = (Array.isArray(blok?.referenceTile) ? blok.referenceTile : []).slice(0, 2);

  const getLink = (tile) => {
    if (currentLanguage === 'en' && tile?.link?.story?.translated_slugs?.[0]?.path) {
      return `/en/${tile.link.story.translated_slugs[0].path}`;
    }

    return `/${tile?.link?.cached_url || ''}`;
  };

  if (!tiles.length) {
    return null;
  }


  return (
    <section {...storyblokEditable(blok)} className="py-12 lg:py-20 partner-references-section">
      <Container>
        <FancyTitle
          text={blok?.title}
          tag="h2"
          textAlign="text-left"
          marginBottom="mb-10"
          subtitle={blok?.subtitle}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {tiles.map((tile) => {
            const linkUrl = getLink(tile);
            const label = tile?.link_text || tile?.link?.story?.name || '';

            return (
              <article key={tile._uid} {...storyblokEditable(tile)} className="flex flex-col h-full">
                <Link
                  to={linkUrl}
                  title={tile?.link?.link_title || undefined}
                  aria-label={label}
                  className="block relative overflow-hidden aspect-[16/10]"
                >
                  {tile?.image?.filename && (
                    (() => {
                      const { width, height } = getStoryblokDimensions(tile.image.filename);
                      return (
                        <picture>
                          <source
                            type="image/avif"
                            srcSet={getStoryblokImageSrcSet(tile.image.filename, [400, 600, 800, 1000], 'avif')}
                            sizes="(max-width: 768px) 100vw, 50vw"
                          />
                          <img
                            src={getStoryblokImageUrl(tile.image.filename, 800)}
                            srcSet={getStoryblokImageSrcSet(tile.image.filename, [400, 600, 800, 1000])}
                            sizes="(max-width: 768px) 100vw, 50vw"
                            alt={getStoryblokAlt(tile.image, '')}
                            width={width}
                            height={height}
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="lazy"
                            decoding="async"
                          />
                        </picture>
                      );
                    })()
                  )}

                  <div className="absolute inset-0 bg-black/50 flex items-end justify-center pb-5">
                    {tile?.logo?.filename ? (
                      (() => {
                        const { width, height } = getStoryblokDimensions(tile.logo.filename);
                        return (
                          <img
                            src={tile.logo.filename}
                            alt={getStoryblokAlt(tile.logo, tile?.link?.story?.name || '')}
                            width={width || 175}
                            height={height || 64}
                            className="max-h-14 max-w-[55%] object-contain"
                            loading="lazy"
                            decoding="async"
                          />
                        );
                      })()
                    ) : (
                      <span className="text-white text-xl font-bold text-center px-4">
                        {tile?.link?.story?.name}
                      </span>
                    )}
                  </div>
                </Link>

                <div className="pt-4 flex flex-col flex-1">
                  {tile?.description && (
                    <p className="text-base mb-3 leading-relaxed">{tile.description}</p>
                  )}

                  {tile?.link_text && (
                    <Link
                      to={linkUrl}
                      title={tile?.link?.link_title || undefined}
                      aria-label={tile.link_text}
                      className="text-[19px] text-latori-pink hover:opacity-75 transition-opacity mt-auto font-bold"
                    >
                      {tile.link_text}
                    </Link>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </Container>
    </section>
  );
}
